import { ZipcodeFetcher } from '@/catalog/types/zipcode-fetcher';
import Spacer from '@/common/components/spacer';
import { validateZipcode } from '@/common/utils/validate-zipcode';
import { InputWithIcon } from '@/form/components/input-with-icon';
import { fullstoryEvent } from '@/fullstory/utils/fullstory-event';
import LocationArrow from '@/icons/location-arrow';
import Spinner from '@/icons/spinner';
import { Button } from '@/shadcn-ui/button';
import { cn } from '@/shadcn-ui/utils';
import { useFetcher } from 'react-router';
import { useEffect, useState, ChangeEvent, useMemo } from 'react';
import { useFullstoryPageLoadEvent } from '@/fullstory/hooks/use-fullstory-page-load-event';

export interface StepZipcodeProps {
  selectedZipcode?: string;
  submitZipcode: (zipcode: string) => void;
  multiStep?: boolean;
}

export default function StepZipcode({
  selectedZipcode,
  submitZipcode,
  multiStep = false,
}: StepZipcodeProps) {
  const [zipcodeError, setZipcodeError] = useState<string | undefined>();
  const [loadingLocationAndZip, setLoadingLocationAndZip] = useState<boolean>(false);
  const [zipcode, setZipcode] = useState(selectedZipcode || '');

  const {
    submit: geolocationSubmit,
    data: zipcodeData,
    state,
  } = useFetcher<ZipcodeFetcher>();

  const isZipValid = useMemo(() => validateZipcode(zipcode), [zipcode]);

  useFullstoryPageLoadEvent('zipcode_modal_shown');

  useEffect(() => {
    if (zipcodeData?.zipcode) {
      submitZipcode(zipcodeData.zipcode);
      setLoadingLocationAndZip(false);
    }
  }, [state, submitZipcode, zipcodeData]);

  const handleGetLocationAndZip = () => {
    setLoadingLocationAndZip(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          geolocationSubmit(
            {
              intent: 'find_nearest_zipcode',
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            },
            { method: 'post' }
          );
        },
        // Error or user denied location access
        () => {
          setLoadingLocationAndZip(false);
          setZipcodeError('Please enter your zipcode manually.');
        }
      );
    }
  };

  const handleZipcodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newZipcode = event.target.value;
    setZipcode(newZipcode);

    // Clear error when user starts typing
    if (zipcodeError) {
      setZipcodeError(undefined);
    }
  };

  const handleZipcodeSubmit = () => {
    if (zipcode && isZipValid) {
      fullstoryEvent('zipcode_added', {
        zipcode: zipcode || 'unk',
      });
      geolocationSubmit(
        {
          intent: 'find_nearest_zipcode',
          zipcode: zipcode,
        },
        { method: 'post' }
      );
      setZipcodeError(undefined);
    } else {
      setZipcodeError('Please enter a valid 5-digit US zip code.');
    }
  };

  const isLoading = state === 'loading' || loadingLocationAndZip;

  return (
    <>
      <p className="body">
        Enter your ZIP code to see accurate availability and delivery options in your
        area.
      </p>

      <Spacer variant="height24" />

      <InputWithIcon
        type="text"
        name="zipcode"
        placeholder="Enter ZIP code"
        icon={
          isLoading ? (
            <Spinner width={24} height={24} />
          ) : (
            <div className="align-center flex flex-row justify-center gap-[8px]">
              <LocationArrow className="relative top-[4px]" />
              <p className="body2-strong relative top-[2px] text-flexcar-blue30">
                Locate me
              </p>
            </div>
          )
        }
        iconContainerClassName={cn('absolute right-[16px] top-[16px]', {
          'pointer-events-none cursor-not-allowed': isLoading,
        })}
        position="right"
        onClickDelegate={handleGetLocationAndZip}
        maxLength={5}
        value={zipcode}
        onChange={handleZipcodeChange}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            handleZipcodeSubmit();
          }
        }}
        onBlur={() => {
          /** Scroll to top of screen when input loses focus. This is necessary for inputs within a modal on mobile devices. Mobile devices have an OS level scroll to input which does not play well with scrollable content under the modal's overlay, where content under overlay will scroll when an input is focused. */
          window.scrollTo(0, 0);
        }}
        containerClassName="w-full"
        className="py-[14px]"
      />

      {zipcodeError || zipcodeData?.errors?.find_nearest_zipcode_error ? (
        <>
          <Spacer variant="height16" />
          <p className="mt-1 text-sm text-flexcar-red50">
            {zipcodeError || zipcodeData?.errors?.find_nearest_zipcode_error}
          </p>
        </>
      ) : null}

      <Spacer variant="height24" />

      <Button
        type="button"
        variant="primary"
        disabled={loadingLocationAndZip || !isZipValid}
        loading={isLoading}
        className="w-full"
        onClick={handleZipcodeSubmit}
      >
        {multiStep ? 'Next' : 'Update'}
      </Button>
    </>
  );
}
